<template>
  <v-content>
    <v-container fluid class="dashboard-container">

      <template v-if="user.role === 'admin'">

        <!-- Encabezado -->
        <v-row no-gutters class="mb-2">
          <v-col cols="12">
            <div class="dashboard-header">
              <div>
                <div class="text-h5 font-weight-medium">
                  Resumen de actividades
                </div>

                <div
                  class="
                    subtitle-2
                    grey--text
                    text--darken-1
                    mt-1
                  "
                >
                  Actividades activas hoy
                  <span v-if="fechaTexto">
                    · {{ fechaTexto }}
                  </span>
                </div>
              </div>

              <v-tooltip bottom>
                <template
                  v-slot:activator="{ on, attrs }"
                >
                  <v-btn
                    icon
                    :loading="loading"
                    v-bind="attrs"
                    v-on="on"
                    @click="refresh"
                  >
                    <v-icon>
                      mdi-refresh
                    </v-icon>
                  </v-btn>
                </template>

                <span>
                  Actualizar información
                </span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>

        <!-- Indicadores -->
        <v-row dense class="mb-3">

          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="metric-card"
            >
              <v-card-text
                class="
                  d-flex
                  align-center
                  justify-space-between
                "
              >
                <div>
                  <div class="metric-label">
                    ACTIVIDADES PROGRAMADAS
                  </div>

                  <div class="metric-value">
                    {{
                      formatearNumero(
                        actProgramadas
                      )
                    }}
                  </div>

                  <div class="metric-caption">
                    Actividades vigentes al día de hoy
                  </div>
                </div>

                <div
                  class="
                    metric-icon
                    metric-icon--primary
                  "
                >
                  <v-icon
                    color="primary"
                    size="30"
                  >
                    mdi-calendar-month
                  </v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="metric-card"
            >
              <v-card-text
                class="
                  d-flex
                  align-center
                  justify-space-between
                "
              >
                <div>
                  <div class="metric-label">
                    ACTIVIDADES RECIBIDAS
                  </div>

                  <div
                    class="
                      metric-value
                      success--text
                    "
                  >
                    {{
                      formatearNumero(
                        actRecibidas
                      )
                    }}
                  </div>

                  <div class="metric-caption">
                    Actividades con al menos un resultado
                  </div>
                </div>

                <div
                  class="
                    metric-icon
                    metric-icon--success
                  "
                >
                  <v-icon
                    color="success"
                    size="30"
                  >
                    mdi-checkbox-marked-outline
                  </v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="metric-card"
            >
              <v-card-text
                class="
                  d-flex
                  align-center
                  justify-space-between
                "
              >
                <div class="avance-general">
                  <div class="metric-label">
                    AVANCE GENERAL
                  </div>

                  <div
                    class="
                      metric-value
                      primary--text
                    "
                  >
                    {{
                      formatearAvance(
                        avance
                      )
                    }}
                  </div>

                  <v-progress-linear
                    :value="avance"
                    height="9"
                    rounded
                    color="primary"
                    background-color="grey lighten-3"
                    class="mt-2"
                  ></v-progress-linear>

                  <div class="metric-caption mt-2">
                    Recibidas respecto a programadas
                  </div>
                </div>

                <div
                  class="
                    metric-icon
                    metric-icon--primary
                  "
                >
                  <v-icon
                    color="primary"
                    size="30"
                  >
                    mdi-trending-up
                  </v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

        </v-row>

        <!-- Gráfica y tabla -->
        <v-row dense>

          <!-- Gráfica -->
          <v-col
            cols="12"
            lg="6"
          >
            <v-card
              outlined
              class="dashboard-detail-card"
            >
              <v-card-title
                class="
                  dashboard-card-title
                  pb-0
                "
              >
                Avance por encuesta
              </v-card-title>

              <v-card-subtitle
                class="
                  dashboard-card-subtitle
                  pb-0
                "
              >
                Encuestas con mayor volumen de
                actividades programadas actualmente
              </v-card-subtitle>

              <v-card-text class="chart-container">
                <apexchart
                  v-if="chartSeries.length"
                  type="bar"
                  height="455"
                  :options="chartOptions"
                  :series="chartSeries"
                ></apexchart>

                <div
                  v-else-if="!loading"
                  class="
                    text-center
                    grey--text
                    py-8
                  "
                >
                  No hay información disponible
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Tabla -->
          <v-col
            cols="12"
            lg="6"
          >
            <v-card
              outlined
              class="dashboard-detail-card"
            >
              <v-card-title
                class="
                  dashboard-table-title
                  py-3
                "
              >
                <span>
                  Detalle por encuesta
                </span>

                <v-spacer></v-spacer>

                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Buscar encuesta..."
                  single-line
                  hide-details
                  dense
                  outlined
                  clearable
                  class="dashboard-search"
                ></v-text-field>
              </v-card-title>

              <v-data-table
                :headers="headers"
                :items="items"
                :loading="loading"
                :search="search"
                :footer-props="footerProps"
                :items-per-page="8"
                item-key="encuesta"
                dense
                class="dashboard-table"
              >
                <v-progress-linear
                  slot="progress"
                  color="primary"
                  indeterminate
                ></v-progress-linear>

                <template
                  v-slot:item.encuesta="{ item }"
                >
                  <v-tooltip bottom>
                    <template
                      v-slot:activator="{ on, attrs }"
                    >
                      <div
                        class="encuesta-nombre"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ item.encuesta }}
                      </div>
                    </template>

                    <span>
                      {{ item.encuesta }}
                    </span>
                  </v-tooltip>
                </template>

                <template
                  v-slot:item.programadas="{ item }"
                >
                  <div class="text-center">
                    {{
                      formatearNumero(
                        item.programadas
                      )
                    }}
                  </div>
                </template>

                <template
                  v-slot:item.recibidas="{ item }"
                >
                  <div class="text-center">
                    {{
                      formatearNumero(
                        item.recibidas
                      )
                    }}
                  </div>
                </template>

                <template
                  v-slot:item.avance="{ item }"
                >
                  <div class="avance-cell">
                    <div class="avance-header">
                      <span>
                        {{
                          formatearAvance(
                            item.avance
                          )
                        }}
                      </span>
                    </div>

                    <v-progress-linear
                      :value="item.avance"
                      height="7"
                      rounded
                      :color="
                        colorAvance(
                          item.avance
                        )
                      "
                      background-color="grey lighten-3"
                    ></v-progress-linear>
                  </div>
                </template>

                <template
                  v-slot:footer.page-text="props"
                >
                  {{
                    props.pageStart
                  }}-{{ props.pageStop }}
                  de
                  {{ props.itemsLength }}
                </template>

                <template v-slot:no-data>
                  No hay información disponible
                </template>

                <template v-slot:no-results>
                  No se encontraron encuestas
                </template>
              </v-data-table>
            </v-card>
          </v-col>

        </v-row>

        <!-- Nota -->
        <v-row dense class="mt-2">
          <v-col cols="12">
            <v-alert
              text
              dense
              type="info"
              class="dashboard-info mb-0"
            >
              El avance considera únicamente
              actividades activas cuyo periodo de
              vigencia incluye el día de hoy.
              Una actividad se considera recibida
              cuando existe al menos un resultado
              asociado.
            </v-alert>
          </v-col>
        </v-row>

      </template>

      <template v-else>
        En construcción...
      </template>

      <v-snackbar
        v-model="error"
        color="error"
        :timeout="3500"
        bottom
        right
      >
        {{ msgError }}
      </v-snackbar>

    </v-container>
  </v-content>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import services from "@/utils/services";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";

Vue.component(
  "apexchart",
  VueApexCharts
);

export default {
  name: "home",

  data: () => ({
    loading: false,
    error: false,
    msgError: "",

    fecha: null,
    actProgramadas: 0,
    actRecibidas: 0,
    avance: 0,

    search: "",
    items: [],

    footerProps: {
      "items-per-page-text":
        "Filas por página",
      "items-per-page-options":
        [8, 15, 25, 50, -1],
    },

    headers: [
      {
        text: "Encuesta",
        value: "encuesta",
        align: "start",
        sortable: true,
        width: "260px",
      },
      {
        text: "Actividades programadas",
        value: "programadas",
        align: "center",
        sortable: true,
        width: "115px",
      },
      {
        text: "Actividades recibidas",
        value: "recibidas",
        align: "center",
        sortable: false,
        width: "130px",
      },
      {
        text: "Avance",
        value: "avance",
        align: "center",
        sortable: true,
        width: "125px",
      },
    ],
  }),

  mounted() {
    moment.locale("es");
    this.refresh();
  },

  computed: {
    ...mapGetters(
      "general",
      [
        "user",
      ]
    ),

    fechaTexto() {
      if (!this.fecha) {
        return "";
      }

      return moment.utc(
        this.fecha
      ).format(
        "D [de] MMMM [de] YYYY"
      );
    },

    /*
     * La gráfica muestra únicamente las
     * encuestas de mayor volumen.
     *
     * La tabla conserva absolutamente todas.
     */
    detalleGrafica() {
      return [...this.items]
        .sort(
          (a, b) =>
            Number(b.programadas) -
            Number(a.programadas)
        )
        .slice(0, 10);
    },

    chartSeries() {
      if (
        !this.detalleGrafica.length
      ) {
        return [];
      }

      return [
        {
          name: "Avance",
          data:
            this.detalleGrafica.map(
              item =>
                Number(
                  item.avance
                ) || 0
            ),
        },
      ];
    },

    chartOptions() {
      return {
        chart: {
          type: "bar",
          toolbar: {
            show: false,
          },
          animations: {
            enabled: true,
          },
        },

        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 3,
            barHeight: "48%",
          },
        },

        dataLabels: {
          enabled: true,

          formatter: value =>
            `${
              Number(value)
                .toFixed(1)
            }%`,

          style: {
            fontSize: "11px",
          },
        },

        xaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,

          categories:
            this.detalleGrafica.map(
              item =>
                item.encuesta
            ),

          labels: {
            formatter: value =>
              `${Math.round(value)}%`,
          },

          title: {
            text: "Avance (%)",
          },
        },

        yaxis: {
          labels: {
            maxWidth: 330,
            style: {
              fontSize: "11px",
            },
          },
        },

        tooltip: {
          custom: ({
            dataPointIndex,
          }) => {
            const item =
              this.detalleGrafica[
                dataPointIndex
              ];

            if (!item) {
              return "";
            }

            return `
              <div
                style="
                  padding:10px 12px;
                  font-size:13px;
                  max-width:300px;
                "
              >
                <strong>
                  ${item.encuesta}
                </strong>

                <br>

                Programadas:
                ${this.formatearNumero(
                  item.programadas
                )}

                <br>

                Recibidas:
                ${this.formatearNumero(
                  item.recibidas
                )}

                <br>

                Avance:
                ${this.formatearAvance(
                  item.avance
                )}
              </div>
            `;
          },
        },

        grid: {
          borderColor: "#eeeeee",
        },
      };
    },
  },

  methods: {
    async refresh() {
      if (
        this.user.role !== "admin"
      ) {
        return;
      }

      this.loading = true;

      try {
        const res =
          await services.ws(
            "get",
            "dashboardV2",
            {}
          );

        if (
          !res ||
          !res.ok
        ) {
          this.error = true;

          this.msgError =
            res &&
            res.message
              ? res.message
              : (
                "No fue posible " +
                "obtener el resumen"
              );

          return;
        }

        this.fecha =
          res.data.fecha;

        this.actProgramadas =
          Number(
            res.data
              .act_programadas
          ) || 0;

        this.actRecibidas =
          Number(
            res.data
              .act_recibidas
          ) || 0;

        this.avance =
          Number(
            res.data.avance
          ) || 0;

        this.items =
          Array.isArray(
            res.data.detalle
          )
            ? res.data.detalle.map(
                item => ({
                  encuesta:
                    item.encuesta,

                  programadas:
                    Number(
                      item.programadas
                    ) || 0,

                  recibidas:
                    Number(
                      item.recibidas
                    ) || 0,

                  avance:
                    Number(
                      item.avance
                    ) || 0,
                })
              )
            : [];

      } catch (err) {
        console.error(err);

        this.error = true;
        this.msgError =
          "No fue posible obtener el resumen";

      } finally {
        this.loading = false;
      }
    },

    formatearNumero(value) {
      return new Intl.NumberFormat(
        "es-MX"
      ).format(
        Number(value) || 0
      );
    },

    formatearAvance(value) {
      return `${
        Number(
          value || 0
        ).toFixed(1)
      }%`;
    },

    colorAvance(value) {
      const valor =
        Number(value) || 0;

      if (valor >= 90) {
        return "success";
      }

      if (valor >= 60) {
        return "primary";
      }

      if (valor >= 30) {
        return "warning";
      }

      return "error";
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 0 20px 14px;
  margin-top: -22px;
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0;
  padding-top: 0;
}

.metric-card {
  height: 100%;
}

.metric-card .v-card__text {
  min-height: 122px;
  padding: 17px 20px;
}

.metric-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.55px;
  color: #757575;
}

.metric-value {
  margin-top: 5px;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 500;
  color: #212121;
}

.metric-caption {
  margin-top: 6px;
  font-size: 12px;
  color: #757575;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  margin-left: 14px;
  border-radius: 50%;
}

.metric-icon--primary {
  background: rgba(
    33,
    150,
    243,
    0.10
  );
}

.metric-icon--success {
  background: rgba(
    76,
    175,
    80,
    0.12
  );
}

.avance-general {
  flex: 1;
  min-width: 0;
}

.dashboard-detail-card {
  height: 100%;
}

.dashboard-card-title {
  padding-top: 12px;
  padding-left: 16px;
  font-size: 18px;
}

.dashboard-card-subtitle {
  padding-left: 16px;
  font-size: 12px;
}

.chart-container {
  padding:
    0 4px
    2px 0;
}

.dashboard-table-title {
  min-height: 68px;
  font-size: 18px;
}

.dashboard-search {
  max-width: 290px;
  margin-left: 15px;
}

.encuesta-nombre {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avance-cell {
  min-width: 95px;
  padding: 4px 0;
}

.avance-header {
  margin-bottom: 4px;
  text-align: left;
  font-size: 12px;
}

.dashboard-info {
  font-size: 12px;
}

::v-deep .dashboard-table th {
  padding:
    0 8px !important;
  font-size: 11px;
  font-weight: 600;
  white-space: normal;
  line-height: 1.2;
  vertical-align: middle;
}

::v-deep .dashboard-table th.text-center {
  text-align: center !important;
}

::v-deep .dashboard-table th:nth-child(2),
::v-deep .dashboard-table th:nth-child(3),
::v-deep .dashboard-table th:nth-child(4) {
  text-align: center !important;
}

::v-deep .dashboard-table td {
  padding:
    0 8px !important;
  font-size: 12px;
  vertical-align: middle;
}

::v-deep .dashboard-table
  tbody tr {
  height: 43px;
}

::v-deep .dashboard-table
  .v-data-footer {
  min-height: 50px;
}

@media (max-width: 1264px) {
  .dashboard-container {
    padding: 12px;
  }

  .dashboard-search {
    max-width: 260px;
  }
}

@media (max-width: 960px) {
  .dashboard-search {
    max-width: none;
    width: 100%;
    margin:
      10px 0 0 0;
  }

  .dashboard-table-title {
    align-items: flex-start;
  }
}
</style>
